"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { MetaSummary } from "@/components/tools/stats-research/meta-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type EffectScale,
  effectsFromCounts,
  effectsFromInterval,
  effectsFromMeans,
  effectsFromSe,
  type LineProblem,
  parseStudyRows,
  type StudyEffects,
  type StudyProblemReason,
} from "@/lib/tool-kit/stats-research/meta-analysis";

/** English strings. A translated content file overrides these through `ui`. */
export const META_ANALYSIS_CALCULATOR_STRINGS = {
  modeLabel: "Your studies report",
  modeEffects: "Effect sizes with a CI or SE",
  modeCounts: "Events and totals (2 × 2)",
  modeMeans: "Means, SDs and group sizes",
  formatLabel: "Each row gives",
  formatInterval: "Effect, lower and upper 95% limits",
  formatSe: "Effect and standard error",
  scaleLabel: "Scale",
  scaleAdditive: "Additive: mean difference, Hedges' g, log ratio",
  scaleRatio: "Ratio: odds, risk or hazard ratio",
  dataLabelInterval:
    "One study per line: name, effect, lower 95% limit, upper 95% limit",
  dataLabelSe: "One study per line: name, effect, standard error",
  dataLabelCounts:
    "One study per line: name, then events and total in group 1, then events and total in group 2",
  dataLabelMeans:
    "One study per line: name, then mean, SD and n for group 1, then mean, SD and n for group 2",
  dataHint:
    "Paste from a spreadsheet or separate values with tabs, commas or semicolons; the name is optional and a header row is skipped. Use tabs or semicolons if your numbers have decimal commas. Group 1 is the treatment or exposed group.",
  ratioSeHint:
    "With ratios, give the standard error of the natural log of the ratio — the SE that regression output reports.",
  placeholderInterval: "Study A, 0.82, 0.70, 0.96\nStudy B, 0.91, 0.75, 1.10",
  placeholderSe: "Study A, 4.2, 1.6\nStudy B, 2.1, 1.1",
  placeholderCounts: "Trial A, 12, 100, 25, 100\nTrial B, 8, 60, 18, 62",
  placeholderMeans: "Study A, 24.5, 6.1, 32, 21.0, 5.8, 30",
  exampleInterval:
    "Study\tHR\tLower\tUpper\nCohort A\t0.82\t0.70\t0.96\nCohort B\t0.91\t0.75\t1.10\nCohort C\t0.68\t0.52\t0.89\nCohort D\t0.88\t0.79\t0.98",
  exampleSe:
    "Study\tMean difference\tSE\nSchool A\t4.2\t1.6\nSchool B\t2.1\t1.1\nSchool C\t5.8\t2.3\nSchool D\t1.4\t0.9",
  exampleCounts:
    "Study\tEvents (treatment)\tTotal (treatment)\tEvents (control)\tTotal (control)\nTrial A\t12\t100\t25\t100\nTrial B\t8\t60\t18\t62\nTrial C\t38\t250\t41\t245\nTrial D\t4\t40\t11\t38\nTrial E\t20\t150\t38\t148",
  exampleMeans:
    "Study\tMean 1\tSD 1\tn 1\tMean 2\tSD 2\tn 2\nStudy A\t24.5\t6.1\t32\t21.0\t5.8\t30\nStudy B\t18.2\t4.9\t45\t17.1\t5.2\t44\nStudy C\t30.1\t7.4\t28\t25.9\t7.9\t27\nStudy D\t22.7\t5.5\t60\t21.8\t5.1\t61",
  loadExample: "Load example",
  clear: "Clear",
  studyName: "Study {number}",
  studiesRead: "one: {count} study read.|other: {count} studies read.",
  headerSkipped: "The first line was read as a header row and skipped.",
  problemCount:
    "Line {line}: expected {expected} numbers after the study name.",
  problemNumber: "Line {line}: one of the values is not a number.",
  problemInterval:
    "Line {line}: the limits must satisfy lower < upper, with the effect between them.",
  problemPositiveRatio:
    "Line {line}: a ratio and its limits must be greater than 0.",
  problemPositiveSe: "Line {line}: the standard error must be greater than 0.",
  problemCounts:
    "Line {line}: events and totals must be whole numbers, with events no larger than the total.",
  problemNoInformation:
    "Line {line}: no events (or only events) in both groups, so the study says nothing about the odds ratio and is left out.",
  problemSpread:
    "Line {line}: both standard deviations must be greater than 0.",
  problemSize:
    "Line {line}: each group needs a whole-number size of at least 2.",
  needTwo: "Pooling needs at least two usable studies.",
  enterPrompt:
    "Paste two or more studies to see the pooled estimates, heterogeneity statistics and a forest plot.",
  correctedNote:
    "† A zero cell in this study's table, so 0.5 was added to each of its four cells.",
  results: "Results",
  studiesHeading: "Studies",
  columnStudy: "Study",
  columnEstimate: "Estimate",
  columnInterval: "95% CI",
  columnWeightFixed: "Weight (fixed)",
  columnWeightRandom: "Weight (random)",
  columnModel: "Model",
  columnZ: "z",
  columnP: "p",
  modelFixed: "Fixed effect (inverse variance)",
  modelRandom: "Random effects (DerSimonian–Laird)",
  randomCard: "Random-effects estimate",
  fixedCard: "Fixed-effect estimate",
  i2Card: "I²",
  tau2Card: "τ²",
  qCard: "Q (df = {df})",
  predictionCard: "95% prediction interval",
  heterogeneityText:
    "Q = {q} on {df} degrees of freedom, p = {p}. I² = {i2}%: that share of the variation between study estimates is attributed to real differences rather than chance, and τ² = {tau2} estimates the variance of the true effects.",
  tauLogScale: "With ratios, τ² is on the natural-log scale.",
  fewStudies:
    "With fewer than about ten studies, Q has little power and τ² is imprecise, so read the heterogeneity statistics with caution.",
  predictionText:
    "The 95% prediction interval, {lower} to {upper}, is where the true effect in a new study like these would be expected to fall.",
  predictionNeedsThree: "A prediction interval needs at least three studies.",
  plotHeading: "Forest plot",
  plotWeights: "Square sizes and weights show",
  plotRandom: "Random-effects weights",
  plotFixed: "Fixed-effect weights",
  plotTitle: "Forest plot",
  headingStudy: "Study",
  headingEstimate: "Estimate [95% CI]",
  headingWeight: "Weight",
  pooledFixed: "Fixed effect",
  pooledRandom: "Random effects",
  axisRatio: "Ratio (log scale)",
  axisOddsRatio: "Odds ratio (log scale)",
  axisHedges: "Hedges' g",
  axisEffect: "Effect",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  fileName: "forest-plot",
  howCalculated: "How this was calculated",
  workingLog:
    "Ratios are pooled as natural logarithms; estimates and limits are converted back with exp() for display.",
  workingFixed:
    "Fixed effect: wᵢ = 1/vᵢ, Σwᵢ = {sumW}; θ = Σwᵢyᵢ / Σwᵢ = {estimate}; SE = 1/√Σwᵢ = {se}",
  workingTau:
    "τ² = max(0, (Q − df) / C), with C = Σwᵢ − Σwᵢ²/Σwᵢ = {c}: max(0, ({q} − {df}) / {c}) = {tau2}",
  workingRandom:
    "Random effects: wᵢ* = 1/(vᵢ + τ²); θ* = Σwᵢ*yᵢ / Σwᵢ* = {estimate}; SE = 1/√Σwᵢ* = {se}",
  report:
    "Random effects (DerSimonian–Laird): {value}, 95% CI [{lower}, {upper}], z = {z}, p {p}; Q({df}) = {q}, p {qp}; I² = {i2}%; τ² = {tau2}.",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

type Strings = typeof META_ANALYSIS_CALCULATOR_STRINGS;
type Mode = "effects" | "counts" | "means";
type Format = "interval" | "se";

const PROBLEM_KEY: Record<StudyProblemReason, keyof Strings> = {
  count: "problemCount",
  number: "problemNumber",
  interval: "problemInterval",
  positiveRatio: "problemPositiveRatio",
  positiveSe: "problemPositiveSe",
  counts: "problemCounts",
  noInformation: "problemNoInformation",
  spread: "problemSpread",
  size: "problemSize",
};

function expectedValues(mode: Mode, format: Format): number {
  if (mode === "counts") {
    return 4;
  }
  if (mode === "means") {
    return 6;
  }
  return format === "interval" ? 3 : 2;
}

function convert(
  mode: Mode,
  format: Format,
  scale: EffectScale,
  text: string
): StudyEffects & { headerSkipped: boolean; read: LineProblem[] } {
  const parsed = parseStudyRows(text, expectedValues(mode, format));
  let effects: StudyEffects;
  if (mode === "counts") {
    effects = effectsFromCounts(parsed.rows);
  } else if (mode === "means") {
    effects = effectsFromMeans(parsed.rows);
  } else if (format === "interval") {
    effects = effectsFromInterval(parsed.rows, scale);
  } else {
    effects = effectsFromSe(parsed.rows, scale);
  }
  return {
    ...effects,
    headerSkipped: parsed.headerSkipped,
    read: parsed.problems,
  };
}

function exampleFor(mode: Mode, format: Format, strings: Strings): string {
  if (mode === "counts") {
    return strings.exampleCounts;
  }
  if (mode === "means") {
    return strings.exampleMeans;
  }
  return format === "interval" ? strings.exampleInterval : strings.exampleSe;
}

function labelFor(mode: Mode, format: Format, strings: Strings) {
  if (mode === "counts") {
    return {
      label: strings.dataLabelCounts,
      placeholder: strings.placeholderCounts,
    };
  }
  if (mode === "means") {
    return {
      label: strings.dataLabelMeans,
      placeholder: strings.placeholderMeans,
    };
  }
  return format === "interval"
    ? {
        label: strings.dataLabelInterval,
        placeholder: strings.placeholderInterval,
      }
    : { label: strings.dataLabelSe, placeholder: strings.placeholderSe };
}

function axisFor(mode: Mode, scale: EffectScale, strings: Strings): string {
  if (mode === "counts") {
    return strings.axisOddsRatio;
  }
  if (mode === "means") {
    return strings.axisHedges;
  }
  return scale === "ratio" ? strings.axisRatio : strings.axisEffect;
}

export default function MetaAnalysisCalculatorWidget() {
  const strings = useToolUi(META_ANALYSIS_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("counts");
  const [format, setFormat] = useState<Format>("interval");
  const [scale, setScale] = useState<EffectScale>("ratio");
  const [texts, setTexts] = useState<Record<Mode, string>>({
    effects: "",
    counts: "",
    means: "",
  });

  const text = texts[mode];
  const setText = (value: string) =>
    setTexts((current) => ({ ...current, [mode]: value }));
  const converted = useMemo(
    () => convert(mode, format, scale, text),
    [mode, format, scale, text]
  );
  const ratio = mode === "counts" || (mode === "effects" && scale === "ratio");
  const problems = [...converted.read, ...converted.problems].sort(
    (a, b) => a.line - b.line
  );
  const expected = expectedValues(mode, format);
  const studies = converted.effects.map((effect, i) => ({
    ...effect,
    label: effect.label ?? t(strings.studyName, { number: String(i + 1) }),
  }));
  const { label, placeholder } = labelFor(mode, format, strings);
  const hasText = text.trim() !== "";

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="ma-mode"
        onChange={setMode}
        options={[
          { value: "counts", label: strings.modeCounts },
          { value: "means", label: strings.modeMeans },
          { value: "effects", label: strings.modeEffects },
        ]}
        value={mode}
      />
      {mode === "effects" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="ma-format"
            label={strings.formatLabel}
            onChange={setFormat}
            options={[
              { value: "interval", label: strings.formatInterval },
              { value: "se", label: strings.formatSe },
            ]}
            value={format}
          />
          <SelectField
            id="ma-scale"
            label={strings.scaleLabel}
            onChange={setScale}
            options={[
              { value: "ratio", label: strings.scaleRatio },
              { value: "additive", label: strings.scaleAdditive },
            ]}
            value={scale}
          />
        </div>
      ) : null}
      <PasteBox
        actions={[
          {
            label: strings.loadExample,
            onClick: () => {
              if (mode === "effects") {
                // The interval example holds hazard ratios; the SE example, mean differences.
                setScale(format === "interval" ? "ratio" : "additive");
              }
              setText(exampleFor(mode, format, strings));
            },
          },
          { label: strings.clear, onClick: () => setText("") },
        ]}
        hint={
          mode === "effects" && format === "se" && scale === "ratio"
            ? `${strings.dataHint} ${strings.ratioSeHint}`
            : strings.dataHint
        }
        id="ma-data"
        label={label}
        onChange={setText}
        placeholder={placeholder}
        rows={8}
        value={text}
      />
      {hasText ? (
        <p className={HINT}>
          {tPlural(strings.studiesRead, locale, studies.length, {
            count: fmt(studies.length),
          }).trim()}{" "}
          {converted.headerSkipped ? strings.headerSkipped : null}
        </p>
      ) : null}
      {problems.length > 0 ? (
        <ErrorNotice>
          {problems
            .map((problem) =>
              t(strings[PROBLEM_KEY[problem.reason]], {
                line: fmt(problem.line),
                expected: fmt(expected),
              })
            )
            .join(" ")}
        </ErrorNotice>
      ) : null}
      {studies.length >= 2 ? (
        <MetaSummary
          axisLabel={axisFor(mode, scale, strings)}
          fmt={fmt}
          ratio={ratio}
          strings={strings}
          studies={studies}
        />
      ) : null}
      {hasText && studies.length < 2 ? (
        <Notice>{strings.needTwo}</Notice>
      ) : null}
      {hasText ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
